import {
  BookedEntity as BookedEntityV2,
  Booking,
  BookingStatus,
  ConferencingDetails,
  ExtendedBooking,
  SelectedPaymentOption,
  ServiceChoices,
} from '@wix/ambassador-bookings-reader-v2-extended-booking/types';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import { Order } from '@wix/ambassador-ecom-v1-order/types';
import { Balance } from '@wix/ambassador-pricing-plans-v2-member-benefit/types';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import {
  BookingDTO,
  Bookings,
  DynamicPriceInfo,
  VideoConference,
} from '../../../types';
import { getServiceIdFromExtendedBooking } from '../../bookInfo';

export const isBookingPaidUsingMembership = (booking: Booking) =>
  booking.selectedPaymentOption === SelectedPaymentOption.MEMBERSHIP ||
  booking.selectedPaymentOption === SelectedPaymentOption.MEMBERSHIP_OFFLINE;

const getServicePricePlans = (service: Service, pricingPlans: Balance[]) =>
  pricingPlans.filter((plan) =>
    service.payment?.pricingPlanIds?.includes(plan.planInfo?.id || ''),
  );

const getServiceUrl = ({
  service,
  flowAPI,
}: {
  service: Service;
  flowAPI: ControllerFlowAPI;
}) => {
  const { wixCodeApi } = flowAPI.controllerConfig;
  return `${wixCodeApi.location.baseUrl}${service.urls?.servicePage?.relativePath}`;
};

export const mapConferencingDetailsToVideoConference = (
  conference: ConferencingDetails,
): VideoConference => ({
  link: conference.guestUrl || '',
  password: conference.password as string,
  description: conference.description as string,
});

export const bookingToBookingDTO = ({
  bookingEntry,
  services,
  businessAddress,
  purchasedPlans,
  flowAPI,
  orders,
}: {
  bookingEntry: ExtendedBooking;
  services: Service[];
  businessAddress?: string | null;
  purchasedPlans?: Balance[];
  flowAPI: ControllerFlowAPI;
  orders?: Order[];
}): BookingDTO => {
  const { booking = {} } = bookingEntry;
  const booked: BookedEntityV2 = booking.bookedEntity!;
  const slot = booked.slot;
  const schedule = booked.schedule;
  const start = booking.startDate;
  const end = booking.endDate;
  let sessionId, staffInfo, locationInfo, locationId, groupId, addOnGroups;
  const dynamicPriceInfo: DynamicPriceInfo = {};

  if (slot) {
    staffInfo = slot?.resource
      ? {
          id: slot.resource.id!,
          fullName: slot.resource.nameTranslated || slot.resource.name!,
        }
      : null;
    locationInfo =
      slot?.location?.formattedAddressTranslated ||
      slot?.location?.formattedAddress ||
      businessAddress;
    locationId = slot?.location?.id;
    sessionId = slot.sessionId;
  } else {
    locationInfo =
      schedule?.location?.formattedAddressTranslated ||
      schedule?.location?.formattedAddress ||
      businessAddress;
    locationId = schedule?.location?.id;
  }

  if (booking?.participantsChoices) {
    const serviceChoices: ServiceChoices[] | undefined =
      booking.participantsChoices.serviceChoices;
    if (
      serviceChoices?.length === 1 &&
      serviceChoices[0]?.numberOfParticipants === 1
    ) {
      const choice = serviceChoices[0]?.choices?.[0];
      if (choice?.custom) {
        dynamicPriceInfo.custom = serviceChoices[0]?.choices?.[0]?.custom;
      } else if (choice?.duration) {
        dynamicPriceInfo.durationName = choice.duration?.name || '';
      }
    } else {
      dynamicPriceInfo.custom = (
        serviceChoices?.map((serviceChoice: ServiceChoices) => {
          return `${serviceChoice.choices?.[0]?.custom} × ${serviceChoice.numberOfParticipants}`;
        }) || []
      ).join(',');
    }
  }

  if (booking?.multiServiceBookingInfo) {
    groupId = booking.multiServiceBookingInfo.id;
  }

  if (booking.bookedAddOns?.length) {
    const { lineItem } = getOrderAndLineItemByBookingId(
      booking.id!,
      orders || [],
    );
    addOnGroups = lineItem?.modifierGroups;
  }

  let relatedService = services?.find(
    (service) => service.id === getServiceIdFromExtendedBooking(bookingEntry),
  );

  let servicePageUrl = '';
  let relatedPricingPlans = [];

  const serviceName =
    booking?.bookedEntity?.titleTranslated || booking?.bookedEntity?.title;

  if (relatedService) {
    relatedService.name = serviceName;

    if (!relatedService.hidden) {
      servicePageUrl = getServiceUrl({ service: relatedService, flowAPI });
    }

    relatedPricingPlans = getServicePricePlans(
      relatedService,
      purchasedPlans || [],
    );
  } else {
    relatedService = { name: serviceName };
  }

  const withCancellationFeePolicy =
    !!bookingEntry.bookingPolicySettings?.cancellationFeeEnabled;

  const videoConference = bookingEntry.conferencingDetails
    ? mapConferencingDetailsToVideoConference(bookingEntry.conferencingDetails)
    : undefined;

  return {
    sessionId,
    locationInfo,
    locationId,
    staffInfo,
    dynamicPriceInfo,
    groupId,
    status: booking!.status || BookingStatus.CONFIRMED,
    contactId: booking?.contactDetails?.contactId!,
    bookingId: booking.id!,
    service: relatedService,
    numOfParticipants: booking.numberOfParticipants,
    start: new Date(start!).getTime(),
    end: new Date(end!).getTime(),
    allowedActions: bookingEntry?.allowedActions!,
    revision: booking.revision,
    withCancellationFeePolicy,
    shouldBlockRescheduleDueToInvalidPricingPlan:
      isBookingPaidUsingMembership(booking) && !relatedPricingPlans?.length,
    ...(videoConference ? { videoConference } : {}),
    servicePageUrl,
    submissionId: bookingEntry.booking?.formSubmissionId,
    sourceAppDefId: bookingEntry.booking?.bookingSource?.appDefId,
    addOnGroups,
  };
};

export const addOrder = ({
  bookings,
  orders,
}: {
  bookings: Bookings;
  orders?: Order[];
}): Bookings => {
  const mappedBookings = {
    ...bookings,
    groups: bookings.groups.map((group) => {
      const { order } = getOrderAndLineItemByBookingId(
        group.bookings[0].bookingId,
        orders || [],
      );

      return {
        ...group,
        order,
      };
    }),
  };

  return mappedBookings;
};

export const getOrderAndLineItemByBookingId = (
  bookingId: string,
  orders: Order[],
) => {
  for (const order of orders) {
    const lineItem = order.lineItems?.find(
      (element) => element.catalogReference?.catalogItemId === bookingId,
    );
    if (lineItem) {
      return {
        order,
        lineItem,
      };
    }
  }

  return {
    order: undefined,
    lineItem: undefined,
  };
};

export const getBasket = ({
  service,
  order,
}: {
  service: Service;
  order?: Order;
}) => {
  const { locations, staffMemberIds } = service;
  const location = locations?.[0];
  const selectedStaff =
    (staffMemberIds?.length ?? 0) > 1 ? 'DO_NOT_CARE' : staffMemberIds?.[0]!;

  return {
    location: {
      type: location?.type!,
      value:
        location?.business?.id ||
        location?.custom?.address?.formattedAddress ||
        '',
    },
    selectedServicesPreferences: [
      {
        service,
        resources: {
          staff: [selectedStaff],
        },
        addOns: order
          ? order.lineItems?.flatMap((lineItem) =>
              lineItem.modifierGroups?.flatMap((modifierGroup) =>
                modifierGroup.modifiers?.map((modifier) => ({
                  groupId: modifierGroup.id,
                  addOnId: modifier.id,
                  quantity: modifier.quantity,
                  price: Number(modifier.price?.amount),
                })),
              ),
            )
          : undefined,
      },
    ],
  };
};
